import type { ParentDigestRow } from '../../api/types';
import { compactGrade } from '../dashboards/sections/format';

const ADDIS = 'Africa/Addis_Ababa';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "Mon 5 Oct" from a YYYY-MM-DD date (calendar arithmetic in UTC, so it never shifts a day). */
export function shortDay(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  return `${DAYS[t.getUTCDay()]} ${t.getUTCDate()} ${MONTHS[t.getUTCMonth()]}`;
}

export function addDays(dateStr: string, n: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** "Mon 5 Oct to Sun 11 Oct" for the week that starts on `weekStart`. */
export function weekRange(weekStart: string): string {
  return `${shortDay(weekStart)} to ${shortDay(addDays(weekStart, 6))}`;
}

/**
 * The Sunday digests next go out (the send is Sunday-guarded in Addis Ababa time): today when it
 * is Sunday there, otherwise the coming one. YYYY-MM-DD.
 */
export function nextSunday(now: Date = new Date()): string {
  const f = new Intl.DateTimeFormat('en-US', { timeZone: ADDIS, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' }).formatToParts(now);
  const get = (t: string) => f.find((p) => p.type === t)?.value ?? '';
  const today = `${get('year')}-${get('month')}-${get('day')}`;
  const dow = DAYS.indexOf(get('weekday'));
  return addDays(today, (7 - dow) % 7);
}

/** Section name without the grade ("Grade 10 B" -> "B"); null when there is no section. */
export function sectionName(row: Pick<ParentDigestRow, 'grade_name' | 'section_display'>): string | null {
  const s = row.section_display;
  if (!s) return null;
  const g = row.grade_name;
  return g && s.startsWith(g) ? s.slice(g.length).trim() || s : s;
}

/** "G10 · B" under a student's name. */
export function classLine(row: Pick<ParentDigestRow, 'grade_name' | 'section_display'>): string {
  const sec = sectionName(row);
  return [compactGrade(row.grade_name) || null, sec].filter(Boolean).join(' · ');
}

/** "Dawit B." for a preview pill. */
export function shortName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.length < 2 ? (parts[0] ?? '') : `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

/** Digest rows for the chosen grade and section (null = all). */
export function audienceRows(rows: ParentDigestRow[], grade: string | null, section: string | null): ParentDigestRow[] {
  return rows.filter((r) => (grade == null || r.grade_name === grade) && (section == null || sectionName(r) === section));
}

/** "Grade 10 · A" / "Grade 10" / "all grades". */
export function audienceLabel(grade: string | null, section: string | null): string {
  if (!grade) return 'all grades';
  return section ? `${grade} · ${section}` : grade;
}
